import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Blinds, Pilcrow, Square, Frame, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mockData';

const Services = () => {
  const serviceDetails = [
    {
      id: 1,
      title: 'Complete Interior Designing',
      icon: Home,
      description: 'End-to-end interior design solutions for residential and commercial spaces. We handle everything from concept development to final execution.',
      features: [
        'Space planning and layout design',
        'Color consultation and material selection',
        'Custom furniture design',
        'Lighting design',
        '3D visualization',
        'Project management'
      ],
      image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80'
    },
    {
      id: 2,
      title: 'Window Blinds (Vista Fashion)',
      icon: Blinds,
      description: 'As authorized Vista Fashion dealers, we offer premium quality window blinds in various styles and materials to suit your needs.',
      features: [
        'Roller blinds',
        'Vertical blinds',
        'Venetian blinds',
        'Zebra blinds',
        'Motorized options',
        'Custom sizes and colors'
      ],
      image: 'https://images.unsplash.com/photo-1616486029423-aaa4789e8c9a?w=800&q=80'
    },
    {
      id: 3,
      title: 'Curtains & Curtain Rods',
      icon: Pilcrow,
      description: 'Elegant curtains and designer curtain rods that complement your interior décor perfectly.',
      features: [
        'Designer curtains',
        'Sheer curtains',
        'Blackout curtains',
        'Custom stitching',
        'Premium curtain rods',
        'Installation service'
      ],
      image: 'https://images.unsplash.com/photo-1557128928-66e3009291b5?w=800&q=80'
    },
    {
      id: 4,
      title: 'Wooden Flooring',
      icon: Square,
      description: 'High-quality wooden flooring solutions that add warmth and elegance to your spaces.',
      features: [
        'Laminate flooring',
        'Engineered wood flooring',
        'Vinyl flooring',
        'Professional installation',
        'Multiple finish options',
        'Maintenance guidance'
      ],
      image: 'https://images.unsplash.com/photo-1534237710431-e2fc698436d0?w=800&q=80'
    },
    {
      id: 5,
      title: 'Wallpapers',
      icon: Frame,
      description: 'Extensive collection of designer wallpapers to transform your walls into stunning features.',
      features: [
        '3D wallpapers',
        'Textured wallpapers',
        'Designer patterns',
        'Custom prints',
        'Professional installation',
        'Easy maintenance options'
      ],
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 via-white to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our{' '}
              <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Services
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Comprehensive interior solutions tailored to your needs and budget
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {serviceDetails.map((service, index) => (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center mb-6">
                    <service.icon className="w-8 h-8 text-amber-600" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <ArrowRight className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact">
                    <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                      Get Quote
                    </Button>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Work Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple, transparent, and efficient
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'Free consultation to understand your requirements' },
              { step: '02', title: 'Design & Quote', description: 'Custom design proposal with transparent pricing' },
              { step: '03', title: 'Execution', description: 'Professional installation with quality materials' },
              { step: '04', title: 'Support', description: 'Post-installation support and warranty' }
            ].map((item, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all relative overflow-hidden">
                <div className="absolute top-0 right-0 text-8xl font-bold text-amber-50">{item.step}</div>
                <CardContent className="p-6 relative z-10">
                  <div className="text-3xl font-bold text-amber-600 mb-3">{item.step}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us today for expert interior design services
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary" className="bg-white text-amber-700 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Us Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
